<template>
  <div class="mc-videohistory-list__history--wrapper">
    <div class="mc-videohistory-list__history--container">
      <div class="mc-videohistory-list__history--text">
        <p>{{ $t('videoHistory.video.listOfVideos') }}</p>
      </div>
      <multiselect
        v-model="camera"
        class="w-50"
        :options="options"
        label="label"
        :placeholder="$t('global.form.select') + ' ' + $t('trackVideo.camera')"
        @select="switchCamera"
      />
    </div>
    <div class="mc-videohistory-videos--container">
      <div class="mc-videohistory-reload--container">
        <div
          v-if="$store.state.vehicle.isLoadingVehicleVideos"
          class="mc-videohistory-reload--loading"
        >
          <b-skeleton />
        </div>

        <div v-else class="mc-videohistory-reload--wrapper">
          <p v-if="vehicleVideoHistory.lastUpdate">
            {{ $t('videoHistory.video.lastUpdate') }}:
            {{ momentDMY_HI(vehicleVideoHistory.lastUpdate) + ' ' + getLabelTimezone() }}
          </p>

          <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
          <p v-else>{{ $t('videoHistory.video.lastUpdate') }}: -</p>

          <span @click="reload">
            <i class="mc-fa icon-fill_refresh"></i>
            {{ $t('videoHistory.video.reload') }}
          </span>
        </div>
      </div>

      <div
        v-if="$store.state.vehicle.isLoadingVehicleVideos"
        class="mc-videohistory-list__data--container"
      >
        <b-spinner label="Loading..." />
      </div>
      <div class="mc-videohistory-list__item--container">
        <div
          v-if="
            !$store.state.vehicle.isLoadingVehicleVideos &&
            vehicleVideoHistory &&
            vehicleVideoHistory.vehicleVideos &&
            vehicleVideoHistory.vehicleVideos.length === 0
          "
          class="mc-videohistory-list__data--container"
        >
          <div class="mc-videohistory-list__no-data--wrapper">
            <img src="@/assets/images/nodata.png" />
            <p class="mc-videohistory-list__no-data--title">
              {{ $t('videoHistory.histories.noData.noDataInSdCard') }}
            </p>
            <p class="mc-videohistory-list__no-data--message">
              {{ $t('videoHistory.histories.noData.selectOtherPeriod') }}
            </p>
          </div>
        </div>

        <div
          v-if="
            !$store.state.vehicle.isLoadingVehicleVideos && vehicleVideoHistory.vehicleVideos.length
          "
          class="mc-videohistory-list__record"
        >
          <div v-for="(vehicle, index) in $store.state.vehicle.vehicleVideos" :key="index">
            <b-button
              v-b-toggle="`collapse-history-${index + 1}`"
              variant="info"
              :class="{
                'mc-videohistory-list__item--collapse': true,
                'mc-videohistory-list__item-collapse--visible': isCollapsed[index],
              }"
              @click="toggleCollapse(index)"
            >
              <span>{{ vehicle.dateTime }} {{ formatTimezone }}</span>
              <span>
                ({{ vehicle.videos.length }})
                <i v-if="isCollapsed[index]" class="mc-fa fa-lg icon-fill_chevron-up"></i>
                <i v-else class="mc-fa fa-lg icon-fill_chevron-down"> </i>
              </span>
            </b-button>
            <b-collapse
              :id="`collapse-history-${index + 1}`"
              v-model="isCollapsed[index]"
              class="mc-videohistory-list__item-history--collapse"
              accordion="histories-accordion"
              role="tabpanel"
            >
              <div class="mc-videohistory-item__data--container">
                <div
                  v-for="(video, indexVideo) in vehicle.videos"
                  :key="indexVideo"
                  :ref="`videoRow${index}-${indexVideo}`"
                  :class="{
                    'mc-videohistory-item__table-row--wrapper': true,
                    'mc-videohistory-item__highlighted':
                      $store.state.vehicle.startTime == video.startTime ||
                      isDownloading(video.startTime),
                  }"
                >
                  <div
                    class="card-text mc-videohistory-item__video-item-row"
                    :class="{ 'last-row': indexVideo === vehicle.videos.length - 1 }"
                  >
                    <div
                      :title="
                        $store.state.vehicle.startTime === video.startTime
                          ? $t('videoHistory.clickForStop')
                          : $t('videoHistory.clickForPlay')
                      "
                      class="history-video-time py-3 px-2"
                      @click="
                        $store.state.vehicle.startTime === video.startTime
                          ? stopStream(video, index, indexVideo)
                          : playStream(video, index, indexVideo)
                      "
                    >
                      <div
                        v-if="
                          $store.state.vehicle.startTime === video.startTime &&
                          ($store.state.vehicle.videoState === 'ready' ||
                            $store.state.vehicle.videoState === 'connecting' ||
                            $store.state.vehicle.videoState === 'sleep')
                        "
                        class="mc-videohistory__dot-play-animation-wrapper"
                      >
                        <PlayDotAnimation />
                      </div>
                      <span v-else class="mc-videohistory-item__video-item-row-dot"></span>
                      <span class="mc-videohistory-item__video-item-row-line"></span>
                      <span class="ms-2">
                        {{ video.time }}
                      </span>
                    </div>
                    <div class="py-3 px-2">
                      <b-spinner v-if="isDownloading(video.startTime)" small label="Loading..." />

                      <McButton
                        v-else
                        :icon="'icon-outline_download'"
                        variant="outline-primary"
                        text=""
                        size="sm"
                        @click="downloadVideo(video, index, indexVideo)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>

        <HistoriesFilter />
      </div>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters, mapState } from 'vuex'
import responsive from '@/mixin/responsive'
import Multiselect from 'vue-multiselect'
import HistoriesFilter from './Filter'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import McButton from '@/components/McButton'
import PlayDotAnimation from './PlayAnimation.vue'
moment.locale('id')

export default {
  name: 'HistoryList',
  components: {
    Multiselect,
    HistoriesFilter,
    McButton,
    PlayDotAnimation,
  },
  mixins: [Helper, responsive, MixPanel],
  data() {
    return {
      cameraButtonStatus: {
        cameraOut: true,
        cameraIn: false,
      },
      camera: null,
      options: [],
      isCollapsed: [],
      statusDeviceStream: '',
      uploadStatus: '',
      isReplaylist: false,
      param: null,
      timeoutPlayStream: null,
    }
  },
  computed: {
    ...mapState('vehicle', ['statusText', 'listVehicle', 'streamingEndpoint']),
    ...mapGetters('vehicle', [
      'vehicleVideoHistory',
      'vehicleDetail',
      'vehicleId',
      'pickVideo',
      'vehiclePeriod',
      'videoStates',
      'streamChanged',
      'vehicleVideoUpload',
      'streamVehicleStatus',
      'vehicleCameraPosition',
      'vehicleStatusByVehicleId',
      'isUsingWsFlv',
    ]),
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    isVideoDemo() {
      if (!this.$route.query?.vehicleId) return false
      const dataDemoVideoHistory = this.getFeatureFlagValue('vsms2_trackvision2_playback_demo_id')
      return Object.keys(dataDemoVideoHistory).includes(this.$route.query?.vehicleId?.toString())
    },
    formatTimezone() {
      if (this.$store.state.vehicle.deviceTimezone === 7) return 'WIB'
      if (this.$store.state.vehicle.deviceTimezone === 8) return 'WITA'
      if (this.$store.state.vehicle.deviceTimezone === 9) return 'WIT'
      return 'WIB'
    },
    isVssHowen() {
      return this.$store.state.vehicle.vehicleDetail.deviceName === 'vss-howen'
    },
    isVideoStatusOnline() {
      const vehicleId = parseInt(this.$route.query?.vehicleId)
      if (!vehicleId || this.listVehicle.length === 0) return false

      const vehicleStatus = this.getVehicleStatus

      if (this.isVideoDemo) return true
      if (!vehicleStatus) return false

      const secondEngineOn = this.getFeatureFlagValue('trackvision_video_offline_acc_on') || 60,
        secondEngineOff = this.getFeatureFlagValue('trackvision_video_offline_acc_off') || 600,
        lastReceive = vehicleStatus.lastReceive,
        engineOn = vehicleStatus.engineOn

      return !this.isDeviceTrackvisionOffline(
        secondEngineOn,
        secondEngineOff,
        lastReceive,
        engineOn
      )
    },
    getVehicleStatus() {
      const vehicleId = parseInt(this.$route.query?.vehicleId)
      if (!vehicleId) return null
      const vehicleStatus = this.vehicleStatusByVehicleId(vehicleId)
      if (!vehicleStatus) return null
      if (this.isVideoDemo) {
        return {
          ...vehicleStatus,
          calculatedMotionStatus: 'M',
        }
      }
      return vehicleStatus
    },
    isHasAccessSwitchView() {
      return this.hasAccess(['b3n-trackvision-toggle-video-status:read'])
    },
    isNewView() {
      const isNewView = JSON.parse(localStorage.getItem('live-streaming-switch-view-on')) ?? true
      return isNewView
    },
    isDownloading() {
      return (startTime) => {
        const convertedStartTime = this.getUtcDateFromLocalDate(
          startTime,
          this.$store.state.vehicle.deviceTimezone
        )

        return this.$store.state.vehicle.downloadVideoPerItemStatus.some((data) => {
          const [vehicleId, channel, _startTime] = data.split('_')
          return (
            Number(vehicleId) === this.vehicleId &&
            convertedStartTime === _startTime &&
            Number(channel) === this.calculatedChannel
          )
        })
      }
    },
    isMdvrSleep() {
      return (
        this.isDeviceBsjMdvr(this.vehicleDetail.deviceCode) &&
        this.getVehicleStatus?.calculatedMotionStatus === 'P'
      )
    },
    calculatedChannel() {
      const deviceName = this.$store.state.vehicle.vehicleDetail.deviceName
      return deviceName === 'jc'
        ? Number(this.$store.state.vehicle.channelActive) + 1
        : Number(this.$store.state.vehicle.channelActive)
    },
  },

  watch: {
    '$store.state.vehicle.channelActive'(channel) {
      const cameraPicked = this.options.find((item) => item.value === channel)
      this.camera = cameraPicked
    },
    async statusText(statusText) {
      if (!statusText) return
      this.options = []
      statusText.split(',').forEach((row, index) => {
        this.options.push({
          value: Number(row),
          label: `${this.$t('trackVideo.camera')} ${index + 1}`,
        })
      })

      this.camera = this.options.find(
        (option) => option.value === this.$store.state.vehicle.channelActive
      )
    },
    '$store.state.vehicle.videoState'(val) {
      if (val === 'noVideo' || val === 'error') {
        this.$store.commit('vehicle/setStartTime', '')
      }
    },
    pickVideo: function (newVal, oldVal) {
      if (oldVal) {
        this.isCollapsed[oldVal.index] = false
      }

      this.isCollapsed[newVal.index] = true
      // this.$nextTick(() => {
      //   this.scrollToActiveRow()
      // })
    },
  },

  mounted() {
    this.$store.commit('vehicle/resetDownloadVideoPerItemStatus')
    if (this.statusText) {
      this.options = []
      this.statusText.split(',').forEach((row, index) => {
        this.options.push({
          value: Number(row),
          label: `${this.$t('trackVideo.camera')} ${index + 1}`,
        })
      })
    }
  },
  methods: {
    async requestFileList() {
      if (!this.$store.state.vehicle.vehicleDetail?.imei) return
      await this.$store.dispatch('vehicle/requestUpdateFileList', {
        input: {
          imei: this.$store.state.vehicle.vehicleDetail.imei,
        },
      })
    },

    async requestVehihcleVideos(channel) {
      const filter = {
        vehicleId: this.vehicleId,
        startTime: this.formatDateToUtc(this.vehiclePeriod[0]),
        endTime: this.formatDateToUtc(this.vehiclePeriod[1]),
        channels: [Number(channel)],
        fileListOnly: true,
        useInMemoryDB: true,
      }

      this.$store.commit('vehicle/setLoadingVehicleVideos', true)

      const response = await this.$store.dispatch('vehicle/requestVehicleVideosTv2', {
        ...filter,
        isFromCache: false,
      })

      this.$store.dispatch('vehicle/splitVehicleVideos', {
        videoFiles: response.files,
        lastUpdate: response.lastUpdate,
        filter: {
          startTime: this.formatDateToUtc(this.vehiclePeriod[0]),
          endTime: this.formatDateToUtc(this.vehiclePeriod[1]),
        },
      })
      await this.$store.commit('vehicle/setLoadingVehicleVideos', false)
    },

    async setVideoDemo(channel) {
      this.$store.commit('vehicle/setLoadingVehicleVideos', true)

      await this.delay(500)
      this.$store.dispatch('vehicle/setVehicleVideosDemoV2', {
        vehicleId: this.$route.query.vehicleId.toString(),
        channel,
      })

      this.$store.commit('vehicle/setLoadingVehicleVideos', false)
    },

    async switchCamera(e) {
      const channelSelected = Number(e.value)

      this.$store.commit('vehicle/setVideoStatusDetail', 'noVideo')
      this.$store.commit('vehicle/clearVehicleVideos')
      this.$store.commit('vehicle/clearVideoStates')
      this.$store.commit('vehicle/clearPickVideo')
      this.$store.commit('vehicle/setChannelActive', channelSelected)

      this.$store.commit('vehicle/setLoadingVehicleVideos', true)

      if (this.isMdvrSleep) {
        await this.$emit('sendOnlineCommandWakeup')
        await this.delay(60 * 1000)
      }

      const deviceName = this.$store.state.vehicle.vehicleDetail.deviceName
      if (deviceName === 'bsj' || deviceName === 'vss-howen') {
        await this.requestFileList()
        await this.delay(5000)
      }

      if (this.isVideoDemo) {
        this.setVideoDemo(channelSelected)
      } else {
        this.requestVehihcleVideos(deviceName === 'jc' ? channelSelected + 1 : channelSelected)
      }
    },

    toggleCollapse(index) {
      this.isCollapsed[index] = !this.isCollapsed[index]
    },

    scrollToActiveRow() {
      if (
        this.pickVideo &&
        this.$refs[`videoRow${this.pickVideo.index}-${this.pickVideo.indexVideo}`] &&
        this.$refs[`videoRow${this.pickVideo.index}-${this.pickVideo.indexVideo}`].length > 0
      ) {
        const row = this.$refs[`videoRow${this.pickVideo.index}-${this.pickVideo.indexVideo}`][0]
        row.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    },

    notifStream() {
      switch (this.streamVehicleStatus.toLowerCase()) {
        case 'failed':
          this.$mcToast.error(this.$t('global.alert.text.sdCardStreamFailed'))
          break
        default:
          break
      }
    },

    async playStreamVssHowen(video) {
      this.$store.commit('vehicle/setVideoStatusDetail', 'connecting')
      clearTimeout(this.timeoutPlayStream)
      this.timeoutPlayStream = setTimeout(() => {
        const source = {
          id: this.vehicleDetail.imei,
          startTime: video.startTime.split('T').join(' '),
          endTime: video.endTime.split('T').join(' '),
          channel: video.channel,
          src: '',
        }
        this.$store.commit('vehicle/setSource', source)
      }, 3000)
    },

    // For device JC and BSJ
    async sendOnlineCommand(video, index, indexVideo) {
      const deviceName = this.$store.state.vehicle.vehicleDetail.deviceName
      const deviceCode = this.$store.state.vehicle.vehicleDetail.deviceCode
      const uid = uuidv4()
      let input = {}
      if (deviceName === 'jc') {
        input['jcInput'] = {
          filename: video.mediaFilename,
        }
      } else if (deviceName === 'bsj') {
        input['bsjInput'] = {
          channel: `CH${this.addLeadingZero(video.channel)}`,
          endTime: this.getUtcDateFromLocalDate(
            video.endTime,
            this.$store.state.vehicle.deviceTimezone
          ),
          startTime: this.getUtcDateFromLocalDate(
            video.startTime,
            this.$store.state.vehicle.deviceTimezone
          ),
        }
      }

      const response = await this.$store.dispatch('vehicle/requestStreamFromDevice', {
        status: true,
        imei: this.vehicleDetail.imei,
        streamingEndpoint: this.streamingEndpoint,
        statsDataV2: {
          tvType: 'TV2',
          idRequest: uid,
        },
        ...input,
      })

      if (response.setDeviceStream.message !== 'Sent') {
        this.$store.commit('vehicle/setVideoStatusDetail', 'noVideo')
        const responseMessage = response.setDeviceStream.message
        if (responseMessage === 'There is a playback or streaming going on') {
          return this.$mcToast.error(this.$t('videoHistory.video.playbackOrStreamingGoinOn'))
        }

        return this.$mcToast.error(this.$t('videoHistory.video.streamError'))
      }

      this.$store.commit('vehicle/setIndexVideoActive', [index, indexVideo])
      this.$store.commit('vehicle/setPlaybackUidActive', uid)

      const source = {
        id: this.vehicleDetail.imei,
        startTime: video.startTime,
        src: this.getVideoHistoryUrl(
          deviceCode,
          this.vehicleDetail.imei,
          video.channel,
          this.streamingEndpoint,
          video.startTime,
          video.endTime,
          this.isUsingWsFlv
        ),
      }
      this.$store.commit('vehicle/setSource', source)
    },

    async playStream(video, index, indexVideo) {
      clearTimeout(this.timeoutPlayStream)
      if (
        (!this.isVideoStatusOnline &&
          this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
          !this.isHasAccessSwitchView) ||
        (!this.isVideoStatusOnline && this.isHasAccessSwitchView && this.isNewView)
      ) {
        this.$mcToast.error(this.$t('videoHistory.toastError.videoOffline'))
        return
      }
      this.$store.commit('vehicle/setSource', null)
      if (this.isMdvrSleep) {
        this.$store.commit('vehicle/setVideoStatusDetail', 'sleep')
      } else {
        this.$store.commit('vehicle/setVideoStatusDetail', 'connecting')
      }
      this.$store.commit('vehicle/setStartTime', video.startTime)
      this.$store.commit('vehicle/setIsVideoHasBeenPaused', false)

      this.track('Play Streaming Video History')

      const deviceName = this.$store.state.vehicle.vehicleDetail.deviceName

      if (this.isVideoDemo) {
        const url =
          process.env.VUE_APP_DEMO_ASSET_URL + '/' + this.vehicleId + '/' + video.mediaFilename
        const source = {
          id: this.vehicleDetail.imei,
          startTime: video.startTime,
          src: url,
        }
        this.$store.commit('vehicle/setSource', source)
        await this.delay(500)
        this.$store.commit('vehicle/setIndexVideoActive', [index, indexVideo])
        this.$store.commit('vehicle/setVideoStatusDetail', 'ready')
        return
      }

      if (deviceName === 'vss-howen') {
        this.$store.commit('vehicle/setIndexVideoActive', [index, indexVideo])
        return this.playStreamVssHowen(video)
      }

      try {
        this.$store.commit('vehicle/setIsLoadingVideoPlayer', true)
        this.timeoutPlayStream = setTimeout(async () => {
          this.$store.commit('vehicle/setIsLoadingVideoPlayer', false)
          if (this.isUsingWsFlv) {
            this.$store.commit('vehicle/setIndexVideoActive', [index, indexVideo])
            const source = {
              id: this.vehicleDetail.imei,
              startTime: video.startTime,
              src: this.getVideoHistoryUrl(
                this.$store.state.vehicle.vehicleDetail.deviceCode,
                this.vehicleDetail.imei,
                video.channel,
                this.streamingEndpoint,
                video.startTime,
                video.endTime,
                this.isUsingWsFlv
              ),
            }
            this.$store.commit('vehicle/setSource', source)
          } else {
            this.sendOnlineCommand(video, index, indexVideo)
          }
        }, 1500)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        return this.$mcToast.error(this.$t('videoHistory.video.streamError'))
      }
    },

    pauseStream() {
      this.track('Stop Streaming Video History')
      if (this.$store.state.vehicle.videoState === 'ready') {
        this.$store.commit('vehicle/setVideoStatusDetail', 'pause')
        this.$store.commit('vehicle/setIsVideoHasBeenPaused', true)
      }
    },

    continueStream() {
      if (this.$store.state.vehicle.videoState === 'pause') {
        this.$store.commit('vehicle/setVideoStatusDetail', 'ready')
      }
    },

    // eslint-disable-next-line no-unused-vars
    stopStream(video, index, indexVideo) {
      this.$store.commit('vehicle/setIsLoadingVideoPlayer', false)
      this.$store.commit('vehicle/setVideoStatusDetail', 'noVideo')
      this.$store.commit('vehicle/setSource', null)
      clearTimeout(this.timeoutPlayStream)
      this.timeoutPlayStream = null
    },

    async downloadVideo(video, index, indexVideo) {
      if (
        (!this.isVideoStatusOnline &&
          this.hasAccess(['b3j-trackvision-video-history-status:read']) &&
          !this.isHasAccessSwitchView) ||
        (!this.isVideoStatusOnline && this.isHasAccessSwitchView && this.isNewView)
      ) {
        this.$mcToast.error(this.$t('videoHistory.toastError.videoOffline'))
        return
      }
      const startTime = this.getUtcDateFromLocalDate(
        video.startTime,
        this.$store.state.vehicle.deviceTimezone
      )

      const endTime = video.endTime
        ? this.getUtcDateFromLocalDate(video.endTime, this.$store.state.vehicle.deviceTimezone)
        : null

      this.$store.commit(
        'vehicle/addDownloadVideoPerItemStatus',
        this.vehicleId + '_' + this.calculatedChannel + '_' + new Date(startTime).toISOString()
      )
      const statusText = this.$store.state.vehicle.statusText
      const convertedChannel =
        statusText.split(',').findIndex((status) => Number(status) === Number(this.camera.value)) +
        1

      if (this.isVideoDemo) {
        const downloadId = Object.keys(this.$store.state.vehicle.dvhPollingData).length
        this.$store.commit('vehicle/updateLoadingDownloadVideoHistoriesWidget', true)
        await this.delay(1000)
        this.$mcToast.success(this.$t('videoHistory.processingDownloadVideo'))
        const url =
          process.env.VUE_APP_DEMO_ASSET_URL + '/' + this.vehicleId + '/' + video.mediaFilename

        const _data = {
          licensePlate: this.$store.state.vehicle.vehicleDetail.licensePlate,
          convertedChannel,
          status: 'I',
          deviceTimezone: this.$store.state.vehicle.deviceTimezone,
          downloadId: downloadId,
          downloadOn: this.now(),
          endTime: this.getUtcDateFromLocalDate(
            this.momentAdd(video.startTime, 1, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
            this.$store.state.vehicle.deviceTimezone
          ),
          mediaFilename: video.mediaFilename,
          startTime: this.getUtcDateFromLocalDate(
            video.startTime,
            this.$store.state.vehicle.deviceTimezone
          ),
          vehicleId: this.vehicleId,
          vehicleType: this.$store.state.vehicle.vehicleDetail.vehicleType,
          vehicleCategory: this.$store.state.vehicle.vehicleDetail.vehicleCategory,
          fullname: this.$store.state.identity.whoami.name,
          timezone: this.$store.state.vehicle.deviceTimezone,
          url,
        }
        this.$store.commit('vehicle/addDvhPollingData', {
          downloadId: downloadId,
          data: _data,
        })

        this.$store.commit('vehicle/setShowWidgetDownload', true)
        document.body.classList.add('mc-widget-download')
        await this.delay(5000)
        _data['status'] = 'S'
        this.$store.commit('vehicle/addDvhPollingData', {
          downloadId: downloadId,
          data: _data,
        })
        this.$store.commit(
          'vehicle/removeDownloadVideoPerItemStatus',
          this.vehicleId +
            '_' +
            this.calculatedChannel +
            '_' +
            new Date(video.startTime).toISOString()
        )
        return
      }
      this.track('Click Video History Download', {
        license_plate: this.vehicleDetail.licensePlate,
        filename: `${video.mediaFilename}`,
      })
      const deviceName = this.$store.state?.vehicle?.vehicleDetail?.deviceName
      const isJc = deviceName === 'jc'
      const isBsj = deviceName === 'bsj'
      const isVssHowen = deviceName === 'vss-howen'
      const variables = {
        input: {
          imei: this.$store.state?.vehicle?.vehicleDetail.imei,
          useInMemoryDB: true,
        },
      }

      if (isJc) {
        variables.input['filenames'] = [video.mediaFilename]
        variables.input['channel'] = parseInt(this.camera.value) + 1
      } else if (isBsj) {
        variables.input['channel'] = parseInt(this.camera.value)
        variables.input['startTime'] = startTime
        variables.input['endTime'] = endTime
        variables.input['identifier'] = 'HistoricalVideo'
      } else if (isVssHowen) {
        variables.input['howenInput'] = {
          channel: Number(this.camera.value),
          startTime,
          endTime,
          identifier: 'H',
        }
      }

      this.$store.commit('vehicle/updateLoadingDownloadVideoHistoriesWidget', true)
      this.$store.dispatch('vehicle/requestSetUploadMedia', variables).then(async (data) => {
        if (!data.isSuccessFull) {
          this.$store.commit(
            'vehicle/removeDownloadVideoPerItemStatus',
            this.vehicleId +
              '_' +
              this.calculatedChannel +
              '_' +
              new Date(video.startTime).toISOString()
          )
          this.$mcToast.error(this.$t('videoHistory.videoFailDownload'))
        } else {
          this.$mcToast.success(this.$t('videoHistory.processingDownloadVideo'))
          this.$store.commit('vehicle/setShowWidgetDownload', true)
          document.body.classList.add('mc-widget-download')
          for (let i = 0; i < data.data.length; i++) {
            const dataDvh = data.data[i]
            const _data = {
              licensePlate: this.$store.state.vehicle.vehicleDetail.licensePlate,
              convertedChannel,
              startTime: dataDvh.startTime,
              endTime: dataDvh.endTime,
              downloadOn: dataDvh.downloadOn,
              status: dataDvh.status,
              deviceTimezone: this.$store.state.vehicle.deviceTimezone,
              downloadId: dataDvh.downloadId,
            }
            this.$store.commit('vehicle/addDvhPollingData', {
              downloadId: dataDvh.downloadId,
              data: _data,
            })
            await this.$nextTick()
            if (dataDvh.status === 'S') {
              this.$store.commit(
                'vehicle/removeDownloadVideoPerItemStatus',
                this.vehicleId +
                  '_' +
                  this.calculatedChannel +
                  '_' +
                  new Date(video.startTime).toISOString()
              )
            }
          }
        }
      })
    },

    // TODO : Remove soon case VSS testing
    stopPlayback() {
      this.$store.commit('vehicle/setSource', null)
      this.$store.commit('vehicle/setVideoStatusDetail', 'noVideo')
    },

    async requestVehicleVideos(vehicleId, isFromCache) {
      this.$store.commit('vehicle/clearVideoStates')
      const filter = {
        vehicleId: vehicleId,
        startTime: this.formatDateToUtc(this.vehiclePeriod[0]),
        endTime: this.formatDateToUtc(this.vehiclePeriod[1]),
        channel:
          this.$store.state.vehicle.vehicleDetail.deviceName === 'jc'
            ? this.$store.state.vehicle.channelActive + 1
            : this.$store.state.vehicle.channelActive,
        fileListOnly: true,
        isFromCache,
      }
      const response = await this.$store.dispatch('vehicle/requestVehicleVideosTv2', filter)
      return response
    },

    async reload() {
      this.track('Click Video History Reload', {
        license_plate: this.vehicleDetail.licensePlate,
      })

      // if (this.isVideoDemo) {
      //   await this.$store.commit('vehicle/setLoadingVehicleVideos', true)
      //   await this.delay(500)
      //   await this.$store.commit('vehicle/setLoadingVehicleVideos', false)
      //   return
      // }

      this.$store.commit('vehicle/setLoadingVehicleVideos', true)

      await this.requestFileList()
      if (this.$store.state.vehicle.vehicleDetail.deviceName === 'bsj') {
        await this.delay(5000)
      } else if (this.$store.state.vehicle.vehicleDetail.deviceName === 'jc') {
        await this.delay(5000)
      }

      const response = await this.requestVehicleVideos(this.vehicleId, false)

      await this.$store.dispatch('vehicle/splitVehicleVideos', {
        videoFiles: response.files,
        lastUpdate: response.lastUpdate,
        filter: {
          startTime: this.formatDateToUtc(this.vehiclePeriod[0]),
          endTime: this.formatDateToUtc(this.vehiclePeriod[1]),
        },
      })

      await this.$store.commit('vehicle/setLoadingVehicleVideos', false)
    },
  },
}
</script>
